import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import type React from 'react'
import { useAdmin, type OrgDetail, type OrgMember, type OrgConnection, type Tier } from '@/hooks/useAdmin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ArrowLeft,
  Building2,
  Users,
  Plug,
  CreditCard,
  Pencil,
  Layers,
  ExternalLink,
} from 'lucide-react'

const TIERS: Tier[] = ['free', 'team', 'scale', 'enterprise']

function tierBadgeClass(tier: Tier | string) {
  const map: Record<string, string> = {
    free: 'text-muted-foreground border-border',
    team: 'text-primary border-primary/40 bg-primary/10',
    scale: 'border-border bg-muted/50',
    enterprise: 'text-success border-success/40 bg-success/10',
  }
  return map[tier] ?? 'text-muted-foreground border-border'
}

function tierBadgeStyle(tier: Tier | string): React.CSSProperties {
  if (tier === 'scale') return { color: 'hsl(var(--amber))' }
  return {}
}

function subStatusPill(status: string) {
  if (status === 'active') return 'status-pill status-pill-success'
  if (status === 'trialing') return 'status-pill status-pill-muted'
  if (status === 'past_due' || status === 'unpaid') return 'status-pill status-pill-danger'
  return 'status-pill status-pill-muted'
}

function connStatusPill(status: string) {
  if (status === 'active') return 'status-pill status-pill-success'
  if (status === 'error') return 'status-pill status-pill-danger'
  return 'status-pill status-pill-muted'
}

interface PageData {
  organization: OrgDetail
  members: OrgMember[]
  connections: OrgConnection[]
}

export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { getOrganization, renameOrganization, setTier, setUsageInsights, impersonate } = useAdmin()

  const [data, setData] = useState<PageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Rename dialog
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [renameBusy, setRenameBusy] = useState(false)

  // Tier override dialog
  const [tierOpen, setTierOpen] = useState(false)
  const [tierValue, setTierValue] = useState<Tier>('free')
  const [tierBusy, setTierBusy] = useState(false)

  // Usage Insights admission
  const [usageBusy, setUsageBusy] = useState(false)

  // Impersonate
  const [impersonateBusy, setImpersonateBusy] = useState(false)

  const load = () => {
    if (!id) return
    setLoading(true)
    setError(null)
    getOrganization(id)
      .then(setData)
      .catch(() => setError('Failed to load organization'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleRename = async () => {
    if (!id || !renameValue.trim()) return
    setRenameBusy(true)
    try {
      await renameOrganization(id, renameValue.trim())
      toast.success('Organization renamed')
      setRenameOpen(false)
      load()
    } catch {
      toast.error('Failed to rename organization')
    } finally {
      setRenameBusy(false)
    }
  }

  const handleSetTier = async () => {
    if (!id) return
    setTierBusy(true)
    try {
      await setTier(id, tierValue)
      toast.success(`Tier updated to ${tierValue}`)
      setTierOpen(false)
      load()
    } catch {
      toast.error('Failed to update tier')
    } finally {
      setTierBusy(false)
    }
  }

  const handleUsageInsights = async (enabled: boolean) => {
    if (!id) return
    setUsageBusy(true)
    try {
      await setUsageInsights(id, enabled)
      toast.success(enabled ? 'Usage Insights enabled' : 'Usage Insights disabled')
      load()
    } catch {
      toast.error('Failed to update Usage Insights')
    } finally {
      setUsageBusy(false)
    }
  }

  const openRenameDialog = () => {
    setRenameValue(data?.organization.name ?? '')
    setRenameOpen(true)
  }

  const openTierDialog = () => {
    setTierValue((data?.organization.tier ?? 'free') as Tier)
    setTierOpen(true)
  }

  const handleImpersonate = async () => {
    if (!id) return
    setImpersonateBusy(true)
    try {
      const { redirectUrl } = await impersonate(id)
      const orgName = data?.organization.name ?? ''
      window.open(`${redirectUrl}&name=${encodeURIComponent(orgName)}`, '_blank')
    } catch {
      toast.error('Failed to start impersonation session')
    } finally {
      setImpersonateBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-6 w-40 rounded bg-muted/40 animate-pulse" />
        <div className="h-8 w-64 rounded bg-muted/40 animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-4 w-32 rounded bg-muted/40 animate-pulse mb-3" />
                <div className="h-20 rounded bg-muted/30 animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <Link to="/orgs" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" />
          Organizations
        </Link>
        <Card>
          <CardContent className="p-6 text-destructive text-sm">
            {error || 'Organization not found'}
          </CardContent>
        </Card>
      </div>
    )
  }

  const { organization: org, members, connections } = data

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/orgs"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Organizations
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="brand-wordmark text-2xl">{org.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className={`text-xs capitalize ${tierBadgeClass(org.tier)}`} style={tierBadgeStyle(org.tier)}>
                {org.tier}
              </Badge>
              <span className={subStatusPill(org.subscription_status)}>
                {org.subscription_status || 'none'}
              </span>
              <span className="mono-accent text-muted-foreground">{org.id}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={openRenameDialog}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Rename
          </Button>
          <Button variant="outline" size="sm" onClick={openTierDialog}>
            <Layers className="h-3.5 w-3.5 mr-1.5" />
            Override tier
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleImpersonate}
            disabled={impersonateBusy}
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            {impersonateBusy ? 'Opening…' : 'View as org'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Members */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              Members ({members.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {members.length === 0 ? (
              <p className="px-6 pb-4 text-sm text-muted-foreground">No members</p>
            ) : (
              <div className="divide-y divide-border">
                {members.map((m) => (
                  <div key={m.user_id} className="flex items-center justify-between px-6 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{m.name || m.email}</p>
                      <p className="mono-accent text-muted-foreground truncate">{m.email}</p>
                    </div>
                    <span className="status-pill status-pill-muted ml-3 shrink-0 capitalize">
                      {m.role}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Connections */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Plug className="h-4 w-4 text-muted-foreground" />
              Connections ({connections.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {connections.length === 0 ? (
              <p className="px-6 pb-4 text-sm text-muted-foreground">No connections</p>
            ) : (
              <div className="divide-y divide-border">
                {connections.map((c) => (
                  <div key={c.id} className="px-6 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">{c.label}</p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {c.is_sandbox && (
                          <span className="status-pill status-pill-muted">Sandbox</span>
                        )}
                        <span className={connStatusPill(c.status)}>{c.status}</span>
                      </div>
                    </div>
                    <p className="mono-accent text-muted-foreground truncate">{c.instance_url}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <dt className="mono-accent text-muted-foreground mb-0.5">Tier</dt>
                <dd>
                  <Badge
                    variant="outline"
                    className={`text-xs capitalize ${tierBadgeClass(org.tier)}`}
                    style={tierBadgeStyle(org.tier)}
                  >
                    {org.tier}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="mono-accent text-muted-foreground mb-0.5">Status</dt>
                <dd>
                  <span className={subStatusPill(org.subscription_status)}>
                    {org.subscription_status || 'none'}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="mono-accent text-muted-foreground mb-0.5">Period ends</dt>
                <dd className="text-sm">
                  {org.subscription_current_period_end
                    ? new Date(org.subscription_current_period_end).toLocaleDateString()
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="mono-accent text-muted-foreground mb-0.5">Stripe customer</dt>
                <dd className="mono-accent text-sm truncate">
                  {org.stripe_customer_id ?? '—'}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="mono-accent text-muted-foreground mb-0.5">Stripe subscription</dt>
                <dd className="mono-accent text-sm truncate">
                  {org.stripe_subscription_id ?? '—'}
                </dd>
              </div>
            </dl>

            {/* Usage Insights is gated per-org independently of tier, so it
                sits below the subscription grid rather than inside it. */}
            <div className="mt-4 border-t pt-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Usage Insights</p>
                <p className="text-xs text-muted-foreground">
                  Unlocks the Usage routes, weekly digest, and anomaly alert emails for this org.
                </p>
              </div>
              <Switch
                checked={org.usage_insights_enabled}
                disabled={usageBusy}
                onCheckedChange={handleUsageInsights}
                aria-label="Usage Insights"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename organization</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Organization name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename()
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm">Cancel</Button>
            </DialogClose>
            <Button
              size="sm"
              disabled={renameBusy || !renameValue.trim()}
              onClick={handleRename}
            >
              {renameBusy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tier override dialog */}
      <Dialog open={tierOpen} onOpenChange={setTierOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Override tier</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground mb-3">
              This immediately changes the org's plan without going through Stripe. Use with care.
            </p>
            <Select value={tierValue} onValueChange={(v) => setTierValue(v as Tier)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIERS.map((t) => (
                  <SelectItem key={t} value={t} className="capitalize">
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm">Cancel</Button>
            </DialogClose>
            <Button
              size="sm"
              disabled={tierBusy}
              onClick={handleSetTier}
            >
              {tierBusy ? 'Updating…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
